import React, { useEffect, useState } from "react";

import moment from "moment";

import { getAnalyticsData } from "api/analytics";
import { getSystems } from "api/systems";
import { getCipCleaning, getCipPrediction, getMembranePrediction } from "api/cipData";

import { getGraphDataPointsInterval } from "utils/utils";
import { CIPPrediction, INSIGHT_TYPE, InsightsStrings } from "constants/constantStrings";

import ScreenStatusBox from "components/ScreenStatusBox";

import { getActiveInsight } from "../../../api/activeInsight";
import SmartInsightsEmptyIcon from "../../../assets/Insights/smart_insights_empty.svg";
import PageLoading from "../../../components/PageLoading/";
import { extractSystemId } from "../../../utils/utils.js";
import InsightsSummary from "../InsightsSummary";

import { ActiveInsightsCards } from "./ActiveInsightsCardComponent";

import "../index.scss";

const ActiveInsights = ({ setActiveInsightCount }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [smartInsights, setSmartInsights] = useState([]);
  const [cipPrediction, setCipPrediction] = useState(null);
  const [membranePrediction, setMembranePrediction] = useState(null);
  const [cipCleaning, setCipCleaning] = useState(null);
  const [insightCipData, setInsightCipData] = useState([]);
  const [noDataErrorMsg, setNoDataErrorMsg] = useState("");
  const [isFoulingPopupOpen, setIsFoulingPopupOpen] = useState(false);
  const sysId = extractSystemId();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getActiveInsight(sysId);
        if (response && response.status === 200) {
          const insights = response.data.map(obj => ({ ...obj, type: INSIGHT_TYPE.insights }));
          setSmartInsights(insights);
          setIsLoaded(true);
        }
      } catch (err) {
        setIsLoaded(true);
      }
    };
    const fetchCIPData = async () => {
      try {
        const DurationRanges = {
          startTime: moment().subtract(7, "days").unix(),
          endTime: moment(new Date()).unix()
        };
        const defaultInterval = getGraphDataPointsInterval(DurationRanges);
        const payload = { ...DurationRanges, defaultInterval };
        const { data } = await getAnalyticsData(payload);
        return data;
      } catch (err) {
        setIsLoaded(true);
      }
    };
    const fetchCIPPredictionData = async () => {
      try {
        const cipData = await fetchCIPData();
        const { CIP_Events } = cipData;
        if (CIP_Events === null || CIP_Events?.length === 0) {
          const predictionResponse = await getCipPrediction();
          if (predictionResponse && predictionResponse.status === 200 && predictionResponse.data) {
            if (checkData(predictionResponse.data)) {
              const predictionCip = predictionResponse.data;
              predictionCip.type = INSIGHT_TYPE.prediction;
              setCipPrediction(predictionCip);
            }
          }
        }
      } catch (err) {
        setIsLoaded(true);
      }
    };
    const fetchCIPCleaningData = async () => {
      try {
        const cleaningResponse = await getCipCleaning();
        if (cleaningResponse && cleaningResponse.status === 200 && cleaningResponse.data) {
          if (checkData(cleaningResponse.data.Baselines)) {
            const cleaningCip = cleaningResponse.data;
            cleaningCip.type = INSIGHT_TYPE.cleaning;
            setCipCleaning(cleaningCip);
          }
        }
      } catch (err) {
        setIsLoaded(true);
      }
    };
    const fetchMembranePredictionData = async () => {
      try {
        const response = await getSystems();
        if (response && response.data && response.data.Systems) {
          const System = response.data.Systems.find(system => system.SysId === sysId);
          if (System) {
            const units = System.Units;
            const membranePredictionResponse = await getMembranePrediction();
            if (membranePredictionResponse && membranePredictionResponse.status === 200 && membranePredictionResponse.data) {
              if (checkData(membranePredictionResponse.data)) {
                const predictionMembrane = membranePredictionResponse.data;
                predictionMembrane.type = INSIGHT_TYPE.membranePrediction;
                predictionMembrane.units = units;
                setMembranePrediction(predictionMembrane);
              }
            }
          }
        }
      } catch (error) {
        setIsLoaded(true);
      }
    };
    fetchData();
    fetchCIPPredictionData();
    fetchCIPCleaningData();
    fetchMembranePredictionData();
  }, [sysId]);

  useEffect(() => {
    let concatData = [];
    if (smartInsights.length > 0 || cipPrediction || cipCleaning || membranePrediction) {
      concatData = { ...smartInsights, cipPrediction, cipCleaning, membranePrediction };
    }
    setInsightCipData(concatData);
    const insightCipCount = smartInsights.length + !!cipPrediction + !!cipCleaning + !!membranePrediction;
    setActiveInsightCount(insightCipCount);
  }, [smartInsights, cipPrediction, cipCleaning, membranePrediction]);

  const checkData = insightCipData => Object.values(insightCipData).some(value => value !== null);
  const errorNotificationClickHandler = data => {
    setNoDataErrorMsg(data);
    setIsFoulingPopupOpen(false);
  };
  return (
    <>
      {!isLoaded ? (
        <PageLoading customHeight={"calc(100vh - 150px)"} />
      ) : (
        <>
          <InsightsSummary descriptionText={InsightsStrings.activeInsightsDescription} />
          {noDataErrorMsg && <ScreenStatusBox type="info" message={CIPPrediction.noFouling} onClose={errorNotificationClickHandler} />}
          {checkData(insightCipData) ? (
            <div>
              <div className="insight-card-container">
                {cipCleaning &&
                  React.createElement(ActiveInsightsCards[INSIGHT_TYPE.cleaning], {
                    value: cipCleaning,
                    setNoDataErrorMsg,
                    isFoulingPopupOpen,
                    setIsFoulingPopupOpen
                  })}
                {cipPrediction &&
                  React.createElement(ActiveInsightsCards[INSIGHT_TYPE.prediction], {
                    value: cipPrediction,
                    setNoDataErrorMsg,
                    isFoulingPopupOpen,
                    setIsFoulingPopupOpen
                  })}
              </div>
              <div className="insight-card-container">
                {membranePrediction && (
                  <div>
                    {React.createElement(ActiveInsightsCards[INSIGHT_TYPE.membranePrediction], {
                      value: membranePrediction,
                      setNoDataErrorMsg,
                      isFoulingPopupOpen,
                      setIsFoulingPopupOpen
                    })}
                  </div>
                )}
              </div>
              <div className="insight-card-container">
                {smartInsights && smartInsights.length > 0 && (
                  <div>
                    {smartInsights.map(insight => (
                      <div key={insight.id}>
                        {React.createElement(ActiveInsightsCards[insight.type], {
                          value: insight,
                          setNoDataErrorMsg,
                          isFoulingPopupOpen,
                          setIsFoulingPopupOpen
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-insights">
              <img src={SmartInsightsEmptyIcon} alt="no-insights" />
              <span className="bodyMainHeadingText text-center">{InsightsStrings.noInsight}</span>
              <span className="smalldescriptionText width-40">{InsightsStrings.noInsightDescription}</span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ActiveInsights;
